import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import styled from 'styled-components';
import { Popup, Icon, Header, Grid, Divider } from 'semantic-ui-react';

// One entry per probability card. `accent` colours the gauge arc.
const WEALTH_OPTIONS = [
  { key: '0.5', value: 0.5, label: '50%', accent: '#b8c4ff' },
  { key: '0.75', value: 0.75, label: '75%', accent: '#6b8cff' },
  { key: '0.9', value: 0.9, label: '90%', accent: '#1f2e86' },
];

const GAUGE_RADIUS = 48;
const GAUGE_LENGTH = Math.PI * GAUGE_RADIUS;

const formatMoney = (amount) =>
  `$${Number(amount).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

function Gauge({ value, label, accent }) {
  return (
    <GaugeWrapper>
      <svg viewBox="0 0 120 60" width="100%" role="img" aria-label={label}>
        <path
          d="M 12 60 A 48 48 0 0 1 108 60"
          fill="none"
          stroke="#eeeeee"
          strokeWidth="14"
        />
        <path
          d="M 12 60 A 48 48 0 0 1 108 60"
          fill="none"
          stroke={accent}
          strokeWidth="14"
          strokeDasharray={`${value * GAUGE_LENGTH} ${GAUGE_LENGTH}`}
        />
        {/* Baseline at y=60 sits exactly on the bottom edge of the arch */}
        <text
          x="60"
          y="60"
          textAnchor="middle"
          fontSize="19"
          fontWeight="700"
          fill="#1f2e86"
        >
          {label}
        </text>
      </svg>
    </GaugeWrapper>
  );
}

Gauge.propTypes = {
  value: PropTypes.number.isRequired,
  label: PropTypes.string.isRequired,
  accent: PropTypes.string.isRequired,
};

class WealthResult extends Component {
  renderTitle = (description) => (
    <div style={{ textAlign: 'left' }}>
      <Header size="medium" style={{ margin: '0 0 1rem' }}>
        Target Wealth at Retirement
        <FutureValueLabel>Future (Nominal) Value</FutureValueLabel>
      </Header>
      <Description>{description}</Description>
    </div>
  );

  renderAnnuity = (result) => {
    const { retAge } = this.props;
    const pv =
      result.target_wealth_pv && result.target_wealth_pv.Annuity !== undefined
        ? result.target_wealth_pv.Annuity
        : null;

    return (
      <div>
        <Divider
          style={{ width: '85%', maxWidth: '963px', margin: '1rem auto' }}
        />
        <div style={{ width: '85%', maxWidth: '963px', margin: 'auto' }}>
          {this.renderTitle(
            'Since your post-retirement portfolio is risk-free (i.e. fixed rate of return), how much you need by your retirement age is a certain amount.'
          )}

          <Grid style={{ margin: '1rem 0' }}>
            <Grid.Row columns={3} style={{ padding: 0 }}>
              <Grid.Column />
              <Grid.Column>
                <ResultCard>
                  <CardLabel>
                    {retAge
                      ? `Target wealth at age ${retAge}`
                      : 'Target wealth'}
                    {pv !== null ? (
                      <Popup
                        trigger={
                          <Icon
                            name="question circle outline"
                            style={{ color: 'dimgrey', marginLeft: '3px' }}
                          />
                        }
                        content={`Equivalent to ${formatMoney(
                          pv
                        )} in today's dollars.`}
                        wide
                        position="top center"
                      />
                    ) : null}
                  </CardLabel>
                  <CardValue>{formatMoney(result.Annuity)}</CardValue>
                </ResultCard>
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </div>
      </div>
    );
  };

  renderWealth = (result) => {
    const { retAge } = this.props;
    const base = result['0.5'];

    return (
      <div>
        <Divider
          style={{ width: '85%', maxWidth: '963px', margin: '1rem auto' }}
        />

        <div style={{ width: '85%', maxWidth: '963px', margin: 'auto' }}>
          {this.renderTitle(
            'Each target wealth amount corresponds to a likelihood of sustaining the client’s retirement spending. Results are based on 10,000 simulations of the selected portfolio.'
          )}

          <Header
            size="small"
            style={{
              margin: '1.5rem 0 0',
              textAlign: 'center',
              fontSize: '16px',
            }}
          >
            Likelihood of Success
            <Popup
              trigger={
                <Icon
                  name="question circle outline"
                  style={{
                    color: 'dimgrey',
                    marginLeft: '3px',
                    fontSize: '1em',
                    marginBottom: '5px',
                  }}
                />
              }
              position="right center"
              content="The probability of being able to sustain your net spending needs in retirement."
            />
          </Header>

          <Grid style={{ margin: '0.5rem 0 1rem' }}>
            <Grid.Row columns={3}>
              {WEALTH_OPTIONS.map((option) => {
                const amount = result[option.key];
                const hasAmount = amount !== undefined && amount !== null;
                const pv = result.target_wealth_pv
                  ? result.target_wealth_pv[option.key]
                  : null;
                const diff =
                  hasAmount && option.key !== '0.5' && base !== undefined
                    ? amount - base
                    : null;

                return (
                  <Grid.Column key={option.key}>
                    <ResultCard>
                      <Gauge
                        value={option.value}
                        label={option.label}
                        accent={option.accent}
                      />
                      <CardDivider />
                      <CardLabel>
                        {retAge
                          ? `Target wealth at age ${retAge}`
                          : 'Target wealth'}
                        {pv !== null && pv !== undefined ? (
                          <Popup
                            trigger={
                              <Icon
                                name="question circle outline"
                                style={{
                                  color: 'dimgrey',
                                  marginLeft: '3px',
                                }}
                              />
                            }
                            content={`Equivalent to ${formatMoney(
                              pv
                            )} in today's dollars.`}
                            wide
                            position="top center"
                          />
                        ) : null}
                      </CardLabel>
                      <CardValue>
                        {hasAmount ? formatMoney(amount) : '--'}
                      </CardValue>
                      {diff !== null ? (
                        <CardSub>{`+${formatMoney(diff)} vs. 50%`}</CardSub>
                      ) : null}
                    </ResultCard>
                  </Grid.Column>
                );
              })}
            </Grid.Row>
          </Grid>
        </div>
      </div>
    );
  };

  render() {
    const { result } = this.props;

    if (!result) {
      return null;
    }

    return result.Annuity !== undefined && result.Annuity !== null
      ? this.renderAnnuity(result)
      : this.renderWealth(result);
  }
}

WealthResult.propTypes = {
  result: PropTypes.object,
  retAge: PropTypes.number,
};

WealthResult.defaultProps = {
  result: null,
  retAge: null,
};

export default connect((state) => ({
  result: state.Planning.wealthResult,
  retAge: state.CacheInputs.planning.target_wealth
    ? Number(state.CacheInputs.planning.target_wealth.retAge) || null
    : null,
}))(WealthResult);

const FutureValueLabel = styled.span`
  margin-left: 0.5rem;
  font-size: 15px;
  font-weight: 400;
  color: rgba(0, 0, 0, 0.6);
`;

const Description = styled.div`
  max-width: 55rem;
  font-size: 15px;
  line-height: 1.7;
  color: rgba(0, 0, 0, 0.6);
`;

const ResultCard = styled.div`
  border: 1px solid #e5e7eb;
  background: #fff;
  border-radius: 8px;
  padding: 1.5rem 1.25rem 1.5rem;
  text-align: center;
  min-height: 100%;
`;

const GaugeWrapper = styled.div`
  position: relative;
  width: 204px;
  max-width: 100%;
  margin: 0 auto;
`;

const CardDivider = styled.div`
  height: 1px;
  background: rgba(0, 0, 0, 0.08);
  margin: 1.25rem 0 1rem;
`;

const CardLabel = styled.div`
  font-size: 15px;
  color: rgba(0, 0, 0, 0.6);
`;

const CardValue = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0.35rem 0 0.25rem;
`;

const CardSub = styled.div`
  font-size: 14px;
  color: rgba(0, 0, 0, 0.55);
`;
